import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  Avatar,
  Card,
  CardContent,
  Chip,
  Container,
  Alert,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  NavigateNext,
  NavigateBefore,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  SportsBasketball as SportsBasketballIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import api from '../api/axiosConfig';
import { Link } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { usePositions } from '../utils/positions';

// === Sélecteur visuel Pro / Cantera / Tous ===
const PlayerTypeToggle = ({ value, onChange, t }) => {
  const handleChange = (_event, newValue) => {
    if (newValue !== null) onChange(newValue);
  };
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      sx={{
        height: 56,
        flexShrink: 0,
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          fontWeight: 600,
          gap: 0.75,
          borderColor: 'rgba(0,0,0,0.23)',
          px: 2,
        },
        '& .MuiToggleButton-root.Mui-selected.toggle-all': {
          backgroundColor: alpha('#607d8b', 0.15),
          color: '#37474f',
          borderColor: '#607d8b',
        },
        '& .MuiToggleButton-root.Mui-selected.toggle-pro': {
          backgroundColor: alpha('#ff9800', 0.18),
          color: '#e65100',
          borderColor: '#ff9800',
        },
        '& .MuiToggleButton-root.Mui-selected.toggle-cantera': {
          backgroundColor: alpha('#2e7d32', 0.18),
          color: '#1b5e20',
          borderColor: '#2e7d32',
        },
      }}
    >
      <ToggleButton value="all" className="toggle-all">
        <GroupsIcon fontSize="small" />
        {t('videoGallery.all')}
      </ToggleButton>
      <ToggleButton value="pro" className="toggle-pro">
        <SportsBasketballIcon fontSize="small" />
        {t('videoGallery.pro')}
      </ToggleButton>
      <ToggleButton value="cantera" className="toggle-cantera">
        <SchoolIcon fontSize="small" />
        {t('videoGallery.cantera')}
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

const VideoGallery = () => {
  const { t } = useTranslation();
  const { translateFromDB } = usePositions(t);
  // État pour les vidéos
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // États pour les filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlayerType, setSelectedPlayerType] = useState('all');

  // Utilisateur courant (pour afficher le toggle aux super admin)
  const [user, setUser] = useState(null);
  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) setUser(JSON.parse(stored));
  }, []);

  // Ref pour le cache buster des avatars
  const avatarCacheBuster = useRef(new Date().getTime());


  // Détection mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fonction pour obtenir l'URL de l'avatar
  const getPlayerAvatarUrl = (playerId) => {
    return playerId
      ? `${getMediaBaseUrl()}/photos/${playerId}.png?cache=${avatarCacheBuster.current}`
      : '/path/to/default-player-avatar.png';
  };

  // Récupération des vidéos
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setIsLoading(true);
        const response = await api.get('/api/videos/with-player');
        setVideos(response.data);
        setFilteredVideos(response.data);
        setCurrentVideoIndex(0);
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des vidéos:', err);
        setError(t('videoGallery.loadingError'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // Filtrage des vidéos
  useEffect(() => {
    let filtered = [...videos];

    // Filtre par nom du joueur uniquement
    if (searchTerm) {
      filtered = filtered.filter(video =>
        video.player_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedPlayerType && selectedPlayerType !== 'all') {
      filtered = filtered.filter(
        video => video.player_type?.toLowerCase() === selectedPlayerType
      );
    }

    setFilteredVideos(filtered);
    setCurrentVideoIndex(0);
  }, [searchTerm, videos, selectedPlayerType]);

  // Navigation entre les vidéos
  const handleNextVideo = () => {
    if (filteredVideos.length > 0) {
      setCurrentVideoIndex((prevIndex) => 
        (prevIndex + 1) % filteredVideos.length
      );
    }
  };

  const handlePrevVideo = () => {
    if (filteredVideos.length > 0) {
      setCurrentVideoIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : filteredVideos.length - 1
      );
    }
  };

  const handleVideoSelect = (index) => {
    setCurrentVideoIndex(index);
  };

  // Formater la date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Formater l'âge
  const formatAge = (ageDecimal) => {
    const age = parseFloat(ageDecimal);
    return !isNaN(age) ? `${age.toFixed(1)} ${t('videoGallery.years')}` : '';
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const currentVideo = filteredVideos[currentVideoIndex];

  return (
    <Box sx={{ position: 'relative', pb: isMobile ? 7 : 0 }}>
      {/* Filtros */}
      <Paper elevation={3} sx={{ p: 3, mb: 4, mx: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`${filteredVideos.length} ${t('videoGallery.videos')}`}
            color="primary"
            sx={{ height: '40px' }}
          />
          <TextField
            sx={{ flex: 1, minWidth: 200 }}
            variant="outlined"
            placeholder={t('videoGallery.searchPlayer')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {user?.role?.toLowerCase() === 'super admin' && (
            <PlayerTypeToggle
              value={selectedPlayerType}
              onChange={(val) => setSelectedPlayerType(val)}
              t={t}
            />
          )}
        </Box>
      </Paper>

      {filteredVideos.length === 0 ? (
        <Alert severity="info" sx={{ mx: { xs: 2, sm: 3 } }}>{t('videoGallery.noVideosFound')}</Alert>
      ) : (
        <Grid container spacing={4} sx={{ px: { xs: 2, sm: 3 } }}>
          {/* Reproductor principal */}
          <Grid item xs={12} lg={8}>
            <Paper elevation={3} sx={{ 
              p: 2,
              height: { 
                xs: 'auto', 
                lg: 'calc(100vh - 350px)' 
              },
              minHeight: { lg: '400px' },
              maxHeight: { lg: '600px' },
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Información del jugador */}
              <Box sx={{ mb: 1, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={getPlayerAvatarUrl(currentVideo?.player_id)}
                    alt={currentVideo?.player_name}
                    sx={{ width: 50, height: 50 }}
                  />
                  <Box>
                    <Link
                      to={`/players/${currentVideo?.player_id}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <Typography variant="subtitle1" component="h2" sx={{ '&:hover': { color: '#ff9800' }, lineHeight: 1.2 }}>
                        {currentVideo?.player_name}
                      </Typography>
                    </Link>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {currentVideo?.player_nationalite && (
                          <>
                            {currentVideo.player_nationalite}
                            <span style={{ margin: '0 4px' }}>•</span>
                          </>
                        )}
                        <PersonIcon sx={{ fontSize: 12, mr: 0.3, verticalAlign: 'middle' }} />
                        {translateFromDB(currentVideo?.player_position)}
                        <span style={{ margin: '0 4px' }}>•</span>
                        {formatAge(currentVideo?.player_age_decimal)}
                      </Typography>
                      {/* Affichage des équipes/compétitions */}
                      {currentVideo?.player_competitions_detail && currentVideo.player_competitions_detail.length > 0 ? (
                        <Box sx={{ mt: 0.5 }}>
                          {[...new Set(currentVideo.player_competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                            const equipeData = currentVideo.player_competitions_detail.find(c => c.equipe_nom === equipe);
                            const competitions = currentVideo.player_competitions_detail
                              .filter(c => c.equipe_nom === equipe)
                              .filter((value, index, self) =>
                                index === self.findIndex((c) => c.competition_id === value.competition_id)
                              );
                            const mediaUrl = getMediaBaseUrl();
                            const timestamp = Date.now();

                            return (
                              <Box key={idx} sx={{ mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box
                                    component="img"
                                    src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                                    alt={equipe}
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      objectFit: 'contain',
                                      borderRadius: '2px'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '12px' }}>
                                    {equipe}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexWrap: 'wrap', ml: 2.5 }}>
                                  {competitions.map((comp, compIdx) => (
                                    <Box key={compIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                                      <Box
                                        component="img"
                                        src={`${mediaUrl}/competitions/${comp.competition_id}.jpg?t=${timestamp}`}
                                        alt={comp.competition_nom}
                                        sx={{
                                          width: 14,
                                          height: 14,
                                          objectFit: 'contain',
                                          borderRadius: '2px'
                                        }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                      <Typography variant="caption" sx={{ fontSize: '10px' }}>{comp.competition_nom}</Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          <GroupsIcon sx={{ fontSize: 12, mr: 0.3, verticalAlign: 'middle' }} />
                          {currentVideo?.player_team || 'N/A'}
                          {(currentVideo?.player_league_1 || currentVideo?.player_league_2) && (
                            <span style={{ fontSize: '10px', color: '#999', marginLeft: '4px' }}>
                              ({[currentVideo.player_league_1, currentVideo.player_league_2].filter(Boolean).join(' - ')})
                            </span>
                          )}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Reproductor de video */}
              <Box sx={{ 
                position: 'relative', 
                flex: 1,
                minHeight: 0,
                bgcolor: '#000',
                borderRadius: 1,
                overflow: 'hidden'
              }}>
                <ReactPlayer
                  url={currentVideo?.url}
                  controls
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                />
              </Box>

              {/* Información del video */}
              <Box sx={{ mt: 1, flexShrink: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {currentVideo?.titre}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <CalendarIcon sx={{ fontSize: 12, mr: 0.3, verticalAlign: 'middle' }} />
                  {formatDate(currentVideo?.fecha)}
                </Typography>
                {currentVideo?.detalles && (
                  <Typography
                    variant="caption"
                    sx={{ 
                      mt: 0.5,
                      display: 'block',
                      maxHeight: '40px',
                      overflow: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '3px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,.2)',
                        borderRadius: '3px',
                      }
                    }}
                    dangerouslySetInnerHTML={{ __html: currentVideo.detalles }}
                  />
                )}
              </Box>

              {/* Controles de navegación */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1, flexShrink: 0 }}>
                <IconButton
                  onClick={handlePrevVideo}
                  size="small"
                  sx={{ bgcolor: 'action.hover' }}
                  disabled={filteredVideos.length <= 1}
                >
                  <NavigateBefore />
                </IconButton>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  {currentVideoIndex + 1} / {filteredVideos.length}
                </Typography>
                <IconButton
                  onClick={handleNextVideo}
                  size="small"
                  sx={{ bgcolor: 'action.hover' }}
                  disabled={filteredVideos.length <= 1}
                >
                  <NavigateNext />
                </IconButton>
              </Box>
            </Paper>
          </Grid>

          {/* Lista de videos */}
          <Grid item xs={12} lg={4}>
            <Paper elevation={3} sx={{ 
              p: 2, 
              height: { 
                xs: 'auto', 
                lg: 'calc(100vh - 350px)' 
              },
              minHeight: { lg: '400px' },
              maxHeight: { xs: '400px', lg: '600px' },
              overflow: 'auto'
            }}>
              {filteredVideos.map((video, index) => (
                <Card
                  key={video.video_id}
                  sx={{
                    mb: 2,
                    cursor: 'pointer',
                    bgcolor: index === currentVideoIndex ? 'action.selected' : 'background.paper',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => handleVideoSelect(index)}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Avatar
                        src={getPlayerAvatarUrl(video.player_id)}
                        alt={video.player_name}
                        sx={{ width: 48, height: 48 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>
                          {video.titre}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {video.player_name}
                        </Typography>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                            {video.player_nationalite && (
                              <>
                                {video.player_nationalite}
                                <span style={{ margin: '0 4px' }}>•</span>
                              </>
                            )}
                            <PersonIcon sx={{ fontSize: 14, mr: 0.5 }} />
                            {translateFromDB(video.player_position)}
                            <span style={{ margin: '0 4px' }}>•</span>
                            {formatAge(video.player_age_decimal)}
                          </Typography>
                          {/* Affichage des équipes/compétitions */}
                          {video.player_competitions_detail && video.player_competitions_detail.length > 0 ? (
                            <Box sx={{ mt: 0.3, mb: 0.3 }}>
                              {[...new Set(video.player_competitions_detail.map(c => c.equipe_nom))].slice(0, 1).map((equipe, idx) => {
                                const equipeData = video.player_competitions_detail.find(c => c.equipe_nom === equipe);
                                const competitions = video.player_competitions_detail
                                  .filter(c => c.equipe_nom === equipe)
                                  .filter((value, index, self) =>
                                    index === self.findIndex((c) => c.competition_id === value.competition_id)
                                  );
                                const mediaUrl = getMediaBaseUrl();
                                const timestamp = Date.now();

                                return (
                                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                    <Box
                                      component="img"
                                      src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                                      alt={equipe}
                                      sx={{
                                        width: 14,
                                        height: 14,
                                        objectFit: 'contain',
                                        borderRadius: '2px'
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '11px' }}>
                                      {equipe}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '10px', color: '#999' }}>
                                      ({competitions.map(c => c.competition_nom).join(', ')})
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              <GroupsIcon sx={{ fontSize: 12, mr: 0.3, verticalAlign: 'middle' }} />
                              {video.player_team || 'N/A'}
                              {(video.player_league_1 || video.player_league_2) && (
                                <span style={{ fontSize: '10px', color: '#999', marginLeft: '4px' }}>
                                  ({[video.player_league_1, video.player_league_2].filter(Boolean).join(' - ')})
                                </span>
                              )}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          <CalendarIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                          {formatDate(video.fecha)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default VideoGallery;